import React, { useContext, useEffect } from 'react';

import { Form, notification } from 'antd';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';

import IntlMessages from 'util/IntlMessages';
import BoxContainer from 'components/BoxContainer';
import FilterContainer from 'components/FilterContainer';
import Title from 'components/BoxContainer/components/Title';
import useReportManagerDt from 'packages/utils/hooks/useReportManagerDt';

import TitleReport from '../components/TitleReport';
import { DT_FORMS, KIND_OF_REPORTS } from '../../constants';
import { useTimeTracker } from '../../contexts/TimeTrackerProvider';
import IncidentTrackerFilters from '../components/IncidentTrackerFilters';
import IncidentsReport from '../components/reports/incidents/IncidentsReport';
import useTimeTrackerReportFactory from '../../hooks/useTimeTrackerReportFactory';
import { getReportDownloadMethods } from '../utils/reportDownloadFactory';
import ReportExportButtons from '../components/ReportExportButtons';
import { ReportContext } from '../contexts/ReportContext';

export default function Incidents() {
  const [preparingFile, setPreparingFile] = React.useState(false);
  const intl = useIntl();
  const [form] = Form.useForm();
  const history = useHistory();
  const location = useLocation();
  const isFilterMode = location.search === '';
  const [refresh, setRefresh] = React.useState(false);
  const {
    renderReport,
    onSubmit,
    data,
    next,
    loading: reportLoading,
    queriedPeriod,
    error: errorReport,
  } = useReportManagerDt();
  const { corporation, loading } = useTimeTracker();
  const { selectedIndividualUser, setSelectedIndividualUser } = useContext(ReportContext);
  const { data: dataParsed, error } = useTimeTrackerReportFactory({
    data: data,
    reportType: KIND_OF_REPORTS.ISSUES,
    queriedPeriod,
    reset: !renderReport,
  });

  const downloadMethods = getReportDownloadMethods(KIND_OF_REPORTS.ISSUES);

  const hasErrors = error || errorReport;
  const reportTitle = intl.formatMessage({ id: 'work.direction.issues.report' });
  const pdfParams = {
    data: dataParsed.data,
    title: reportTitle,
    fileName: reportTitle,
    includeGlosary: false,
    orientation: 'landscape',
  };

  const onClearFilters = () => {
    form.resetFields();
    setRefresh(prev => !prev);
  };

  const mainLoading = loading || reportLoading;

  useEffect(() => {
    if (renderReport && hasErrors) {
      notification.error({
        message: intl.formatMessage({ id: 'general.save.error.message' }),
      });
    }
    if (!renderReport && selectedIndividualUser) {
      setSelectedIndividualUser(null);
    }
  }, [renderReport, hasErrors]);

  return (
    <BoxContainer fullContent>
      <BoxContainer fixed>
        <FilterContainer
          titleClassName="gx-w-100"
          goBack={renderReport && history.goBack}
          title={
            <Title.Header
              value={
                <div className="gx-flex-row gx-justify-content-between gx-align-items-center">
                  <TitleReport
                    corporationName={corporation?.name}
                    title={<IntlMessages id="work.direction.issues.report" />}
                    rut={corporation?.RUT}
                  />
                  {renderReport && (
                    <ReportExportButtons
                      downloadMethods={downloadMethods}
                      pdfParams={pdfParams}
                      onDownloadDocx={() =>
                        downloadMethods.downloadDocx({
                          data: dataParsed,
                          fileName: 'issues',
                          reportTitle,
                        })
                      }
                      onDownloadExcel={() =>
                        downloadMethods.downloadExcel({
                          data: dataParsed,
                          fileName: 'issues',
                          reportTitle,
                        })
                      }
                      preparingFile={preparingFile}
                      setPreparingFile={setPreparingFile}
                    />
                  )}
                </div>
              }
            />
          }
          actionButtons={
            isFilterMode && [
              {
                label: <IntlMessages id="work.direction.filter.clear" />,
                action: onClearFilters,
              },
            ]
          }
        />
      </BoxContainer>
      <BoxContainer
        fullContent
        loading={mainLoading}
        onErrorCallback={history.goBack}
        error={hasErrors}
      >
        {!renderReport ? (
          <IncidentTrackerFilters
            onSubmit={onSubmit}
            formName={DT_FORMS.INCIDENTS_REPORT}
            form={form}
            refresh={refresh}
          />
        ) : (
          <IncidentsReport
            data={dataParsed}
            loading={mainLoading}
            next={next}
            queriedPeriod={queriedPeriod}
          />
        )}
      </BoxContainer>
    </BoxContainer>
  );
}
